import {isFeatureEnabled} from '@github-ui/feature-flags'
import {DefaultLabelPickerAnchor as FutureDefaultLabelAnchor} from '@github-ui/item-picker-label/DefaultLabelPickerAnchor'
import {
  BulkIssuesItemPickerLabel as FutureBulkIssuesLabelPicker,
  ItemPickerLabel as FutureLabelPicker,
  ItemPickerLabelGraphqlQuery as FutureLabelPickerGraphqlQuery,
  type ItemPickerLabelProps as FutureLabelPickerProps,
  type ItemPickerLabelRelayItem as FutureLabelPickerLabel,
  readLabelFromFragment as futureReadLabelFromFragment,
} from '@github-ui/item-picker-label-relay'
import {forwardRef} from 'react'

import {
  BulkIssuesLabelPicker as LegacyBulkIssuesLabelPicker,
  DefaultLabelAnchor as LegacyDefaultLabelAnchor,
  LabelPicker as LegacyLabelPicker,
  readLabelFromFragment as legacyReadLabelFromFragment,
} from './LabelPicker'

/**
 * LabelPickerEntrypoint
 *
 * Switches between legacy label picker and relay-based implementation using feature flag
 * `item_picker_label_relay_migration`.
 */

export type LabelPickerLabel = FutureLabelPickerLabel
// Re-export legacy props type for external consumers.
export type LabelPickerProps = FutureLabelPickerProps

// Preserve original exported query name for consumers; always point to future query for forward compatibility.
export const LabelPickerGraphqlQuery = FutureLabelPickerGraphqlQuery

export function LabelPicker(props: React.ComponentProps<typeof FutureLabelPicker>) {
  const ffEnabled = isFeatureEnabled('item_picker_label_relay_migration')
  return ffEnabled ? <FutureLabelPicker {...props} /> : <LegacyLabelPicker {...props} />
}

export function BulkIssuesLabelPicker(props: React.ComponentProps<typeof FutureBulkIssuesLabelPicker>) {
  const ffEnabled = isFeatureEnabled('item_picker_label_relay_migration')
  return ffEnabled ? <FutureBulkIssuesLabelPicker {...props} /> : <LegacyBulkIssuesLabelPicker {...props} />
}

type FutureLabelFragmentKey = Parameters<typeof futureReadLabelFromFragment>[0]
type LegacyLabelFragmentKey = Parameters<typeof legacyReadLabelFromFragment>[0]

export function readLabelFromFragment(fragmentKey: FutureLabelFragmentKey | LegacyLabelFragmentKey): LabelPickerLabel {
  const ffEnabled = isFeatureEnabled('item_picker_label_relay_migration')
  return ffEnabled
    ? futureReadLabelFromFragment(fragmentKey as FutureLabelFragmentKey)
    : legacyReadLabelFromFragment(fragmentKey as LegacyLabelFragmentKey)
}

export type LabelPickerTestQueries = {
  LabelPickerQuery: unknown
}

export const DefaultLabelAnchor = forwardRef<HTMLButtonElement, React.ComponentProps<typeof FutureDefaultLabelAnchor>>(
  (props, ref) => {
    const ffEnabled = isFeatureEnabled('item_picker_label_relay_migration')
    if (ffEnabled) return <FutureDefaultLabelAnchor {...props} ref={ref} />
    return <LegacyDefaultLabelAnchor {...props} ref={ref} />
  },
)

DefaultLabelAnchor.displayName = 'DefaultLabelAnchor'

try{ LabelPicker.displayName ||= 'LabelPicker' } catch {}
try{ BulkIssuesLabelPicker.displayName ||= 'BulkIssuesLabelPicker' } catch {}